import {
  Badge,
  Button,
  Card,
  Field,
  Input,
  Select,
  Spinner,
  useConfirm,
  useToast,
} from '@aurora/ui-kit';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { api } from '../api/client.js';

interface PortalCustomer {
  id: string;
  companyId: string;
  email: string;
  displayName: string | null;
  isActive: boolean;
  lastLoginAt: string | null;
}

interface Company {
  id: string;
  name: string;
}

function formatDateTime(iso: string | null): string {
  return iso
    ? new Date(iso).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' })
    : '—';
}

/** Mitarbeiter-Sicht: wer darf sich ins Kundenportal einloggen (core.portal.manage). */
export default function PortalCustomersPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const confirmDialog = useConfirm();
  const [form, setForm] = useState({ companyId: '', email: '', displayName: '' });

  const customers = useQuery({
    queryKey: ['portalCustomers'],
    queryFn: () => api<{ items: PortalCustomer[] }>('/api/v1/portal/customers'),
  });
  const companies = useQuery({
    queryKey: ['crm', 'companies', 'picker'],
    queryFn: () => api<{ items: Company[] }>('/api/v1/crm/companies?limit=100'),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['portalCustomers'] });

  const create = useMutation({
    mutationFn: () =>
      api('/api/v1/portal/customers', {
        method: 'POST',
        body: {
          companyId: form.companyId,
          email: form.email,
          displayName: form.displayName || null,
          sendInvite: true,
        },
      }),
    onSuccess: () => {
      toast.success('Zugang angelegt — Einladung verschickt');
      setForm({ companyId: '', email: '', displayName: '' });
      void invalidate();
    },
    onError: (error) => toast.error(error.message),
  });

  const invite = useMutation({
    mutationFn: (id: string) => api(`/api/v1/portal/customers/${id}/invite`, { method: 'POST' }),
    onSuccess: () => toast.success('Anmeldelink verschickt'),
    onError: (error) => toast.error(error.message),
  });

  const toggle = useMutation({
    mutationFn: (input: { id: string; isActive: boolean }) =>
      api(`/api/v1/portal/customers/${input.id}`, {
        method: 'PUT',
        body: { isActive: input.isActive },
      }),
    onSuccess: () => void invalidate(),
    onError: (error) => toast.error(error.message),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api(`/api/v1/portal/customers/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.success('Zugang entfernt');
      void invalidate();
    },
    onError: (error) => toast.error(error.message),
  });

  const companyName = (id: string): string =>
    companies.data?.items.find((c) => c.id === id)?.name ?? '—';

  return (
    <div className="page">
      <h1 className="page__title">Kundenportal</h1>
      <p className="page__lead">
        Wer hier einen Zugang hat, kann sich passwortlos per E-Mail-Link anmelden und sieht die
        Rechnungen und Angebote seiner Firma.
      </p>

      <Card>
        <div className="crm-section__title">Zugang anlegen</div>
        <form
          className="crm-form"
          onSubmit={(event) => {
            event.preventDefault();
            if (form.companyId && form.email) create.mutate();
          }}
        >
          <div className="crm-form__row">
            <Field label="Firma">
              {(id) => (
                <Select
                  id={id}
                  required
                  value={form.companyId}
                  onChange={(e) => setForm({ ...form, companyId: e.target.value })}
                >
                  <option value="">— Firma wählen —</option>
                  {(companies.data?.items ?? []).map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label="E-Mail">
              {(id) => (
                <Input
                  id={id}
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              )}
            </Field>
            <Field label="Name (optional)">
              {(id) => (
                <Input
                  id={id}
                  value={form.displayName}
                  onChange={(e) => setForm({ ...form, displayName: e.target.value })}
                />
              )}
            </Field>
          </div>
          <div className="crm-form__actions">
            <Button type="submit" loading={create.isPending}>
              Anlegen und einladen
            </Button>
          </div>
        </form>
      </Card>

      <Card>
        <div className="crm-section__title">Zugänge</div>
        {customers.isLoading ? (
          <Spinner />
        ) : (customers.data?.items.length ?? 0) === 0 ? (
          <p className="crm-timeline__meta">Noch keine Portal-Zugänge angelegt.</p>
        ) : (
          <table className="au-table">
            <thead>
              <tr>
                <th>E-Mail</th>
                <th>Name</th>
                <th>Firma</th>
                <th>Status</th>
                <th>Letzte Anmeldung</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {customers.data!.items.map((customer) => (
                <tr key={customer.id}>
                  <td>{customer.email}</td>
                  <td>{customer.displayName ?? '—'}</td>
                  <td>{companyName(customer.companyId)}</td>
                  <td>
                    <Badge>{customer.isActive ? 'aktiv' : 'deaktiviert'}</Badge>
                  </td>
                  <td>{formatDateTime(customer.lastLoginAt)}</td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <Button
                      size="sm"
                      variant="subtle"
                      disabled={!customer.isActive}
                      loading={invite.isPending}
                      onClick={() => invite.mutate(customer.id)}
                    >
                      Link senden
                    </Button>{' '}
                    <Button
                      size="sm"
                      variant="subtle"
                      onClick={() =>
                        toggle.mutate({ id: customer.id, isActive: !customer.isActive })
                      }
                    >
                      {customer.isActive ? 'Deaktivieren' : 'Aktivieren'}
                    </Button>{' '}
                    <Button
                      size="sm"
                      variant="subtle"
                      onClick={() => {
                        void confirmDialog({
                          title: 'Zugang entfernen?',
                          description: `${customer.email} kann sich danach nicht mehr anmelden.`,
                          confirmLabel: 'Entfernen',
                          danger: true,
                        }).then((ok) => {
                          if (ok) remove.mutate(customer.id);
                        });
                      }}
                    >
                      Löschen
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
